import type { SellerStoreAccess, StoreCapability } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";

/**
 * Capability keys grouped into the features a seller recognises.
 *
 * This is NOT a tier map: which column a feature lands in ("available now" or
 * "with Premium") is read from `seller.access.capabilities`, the backend
 * projection. This list only names things in plain words and fixes their
 * order — what a social seller asks for first comes first.
 *
 * Infrastructure capabilities (wallet, storeSettings, dashboard, onboarding)
 * are not features a seller shops for and are deliberately absent.
 */
export type PlanFeature = {
  capability: StoreCapability;
  label: string;
  /** One line on what it does for the seller. Paid features only. */
  benefit?: string;
  /** Not listed when the store already has this broader capability (the one
   *  moderator seat is part of the full team). */
  subsumedBy?: StoreCapability;
  /** Listed only when the backend reports the capability at all — a newer
   *  capability is never shown as "with Premium" by an older backend. */
  onlyWhenReported?: boolean;
};

const f = keys.plan.features;

/** Core features, listed first in "available now". */
const CORE: PlanFeature[] = [
  { capability: "products", label: f.products.label },
  { capability: "orders", label: f.orders.label },
  { capability: "customers", label: f.customers.label },
  { capability: "checkoutLinks", label: f.checkoutLinks.label },
  { capability: "shippingFees", label: f.shippingFees.label },
  { capability: "logo", label: f.logo.label },
  // Free's one order-moderator seat (D2) — "available now" on Free.
  {
    capability: "teamSeat",
    label: f.teamSeat.label,
    subsumedBy: "team",
    onlyWhenReported: true,
  },
];

/** The rest, in the order a seller weighs them. */
const EXTRA: PlanFeature[] = [
  { capability: "productVariants", label: f.productVariants.label, benefit: f.productVariants.benefit },
  { capability: "categories", label: f.categories.label, benefit: f.categories.benefit },
  { capability: "promotions", label: f.promotions.label, benefit: f.promotions.benefit },
  { capability: "design", label: f.design.label, benefit: f.design.benefit },
  { capability: "reviews", label: f.reviews.label, benefit: f.reviews.benefit },
  { capability: "team", label: f.team.label, benefit: f.team.benefit },
  { capability: "analytics", label: f.analytics.label, benefit: f.analytics.benefit },
  { capability: "onlinePayments", label: f.onlinePayments.label, benefit: f.onlinePayments.benefit },
  { capability: "manualPayments", label: f.manualPayments.label, benefit: f.manualPayments.benefit },
  { capability: "shipping", label: f.shipping.label, benefit: f.shipping.benefit },
  { capability: "productImportExport", label: f.productImportExport.label, benefit: f.productImportExport.benefit },
  { capability: "inventory", label: f.inventory.label, benefit: f.inventory.benefit },
  { capability: "stockNotifications", label: f.stockNotifications.label, benefit: f.stockNotifications.benefit },
  { capability: "returns", label: f.returns.label, benefit: f.returns.benefit },
  { capability: "invoices", label: f.invoices.label, benefit: f.invoices.benefit },
  { capability: "profileBranding", label: f.profileBranding.label, benefit: f.profileBranding.benefit },
  { capability: "advancedProductContent", label: f.advancedProductContent.label, benefit: f.advancedProductContent.benefit },
  { capability: "marketing", label: f.marketing.label, benefit: f.marketing.benefit },
  { capability: "abandonedCartRecovery", label: f.abandonedCartRecovery.label, benefit: f.abandonedCartRecovery.benefit },
  { capability: "customDomain", label: f.customDomain.label, benefit: f.customDomain.benefit },
];

export const PLAN_FEATURES: readonly PlanFeature[] = [...CORE, ...EXTRA];

export function planFeatureFor(
  capability: StoreCapability | undefined,
): PlanFeature | undefined {
  return capability
    ? PLAN_FEATURES.find((feature) => feature.capability === capability)
    : undefined;
}

/** Splits the features by the store's current capabilities. */
export function splitPlanFeatures(access: SellerStoreAccess | undefined): {
  available: PlanFeature[];
  locked: PlanFeature[];
} {
  const available: PlanFeature[] = [];
  const locked: PlanFeature[] = [];
  for (const feature of PLAN_FEATURES) {
    const reported = access?.capabilities[feature.capability];
    if (feature.onlyWhenReported && reported === undefined) continue;
    if (feature.subsumedBy && access?.capabilities[feature.subsumedBy] === true)
      continue;
    if (reported === true) available.push(feature);
    else locked.push(feature);
  }
  return { available, locked };
}
